// tslint:disable-next-line:no-submodule-imports
import {flextree} from 'd3-flextree/build/d3-flextree.min';
import InitialStep from "../../../formability/form_builder/models/workflow/initialStep";
import IWorkflowStepContainer from "../../../formability/form_builder/models/workflow/iworkflowStepContainer";
import {
  ActionCoordinate,
  DisconnectedTitleCoordinate,
  ProcessFlowActionCoordinate,
} from "../../../formability/form_builder/models/workflow/workflowGraph";
import WorkflowStep from "../../../formability/form_builder/models/workflow/workflowStep";
import WorkflowStepAction from "../../../formability/form_builder/models/workflow/workflowStepAction";
import WorkflowStepActionJumpTo from "../../../formability/form_builder/models/workflow/workflowStepActionJumpTo";
import DiagramElement from './diagramElement';
import StepElement from './stepElement';
import ProcessFlowStepJumpTo from "~/models/processFlows/processFlowStepJumpTo";
import ProcessFlowEndElement from "../../../formability/processFlowBuilder/helpers/processFlowDiagram/processFlowEndElement";
import ProcessFlowStepAction from '~/models/processFlows/processFlowStepAction'

export default abstract class WorkareaDiagram {
  abstract initialStep
  initialStepCoords: number[];
  debug: boolean = false;

  abstract get const()
  abstract get initialElementClass()
  abstract get stepClass()
  abstract get stepElementClass()

  abstract get allSteps()
  /**
   * Flag for determining if the "plus" for arrows should show underneath the step
   */
  abstract isLinkConfigurable(step): boolean

  get stepGraphArray(): DiagramElement[] {
    const workflowElements: DiagramElement[] = [];
    const clientIdsSeen = {};
    const initialElement = new this.initialElementClass(null, this.initialStep, false);
    workflowElements.push(initialElement);
    this.generateStepGraphFor(initialElement, workflowElements, clientIdsSeen, this.initialStep, null);
    this.calculateLayout(initialElement, this.initialStepCoords);
    this.generateDisconnectedSteps(workflowElements, clientIdsSeen);
    return workflowElements;
  }

  private calculateLayout(initialCoordinate, coordsOffset) {
    const layout = flextree()
      .nodeSize(n => {
        return [n.data.fullStepWidth, n.data.fullStepHeight];
      });
    const result = layout.hierarchy(initialCoordinate);
    layout(result);
    if (this.debug) console.log(layout.dump(result));
    result.each(n => {
      n.data.x = n.x + coordsOffset[0];
      n.data.y = n.y + coordsOffset[1];
    });
  }

  private generateDisconnectedSteps(workflowCoordinates: DiagramElement[], clientIdsSeen: object) {
    const disconnectedSteps = [];
    const allSteps = this.allSteps;
    for (const clientId in allSteps) {
      if (!clientIdsSeen[clientId]) {
        disconnectedSteps.push(allSteps[clientId]);
      }
    }
    for (let i=0; i < disconnectedSteps.length; i++) {
      const step = disconnectedSteps[i];
      if (!step) continue;
      for (let j=0; j < disconnectedSteps.length; j++) {
        const otherStep = disconnectedSteps[j];
        if (!otherStep || step === otherStep) continue;
        if (step.canJumpTo(otherStep.client_id)) {
          disconnectedSteps.splice(j, 1);
          if (j < i) {
            i--;
          }
          j--;
        }
      }
    }
    if (disconnectedSteps.length > 0) {
      const graphBottom = Math.max(...workflowCoordinates.map(c => c.startY));
      const disconnectedTitleCoordinate = new DisconnectedTitleCoordinate(disconnectedSteps);
      workflowCoordinates.push(disconnectedTitleCoordinate);
      this.generateStepGraphFor(
        disconnectedTitleCoordinate,
        workflowCoordinates,
        clientIdsSeen,
        disconnectedTitleCoordinate,
        null);
      this.calculateLayout(disconnectedTitleCoordinate, [this.initialStepCoords[0], graphBottom + this.const.DisconnectedStepYOffset]);
    }
  }

  brokenWorkFlowCoords(): any {
    let brokenCoords = [];
    const stepGraphArray = this.stepGraphArray;
    if (!this.coordinatesHaveEnds(stepGraphArray)) {
      brokenCoords.push(this.initialStepCoords);
      return brokenCoords
    }
    for (let coordinate of stepGraphArray) {
      if (!(coordinate instanceof StepElement) || !(coordinate.step instanceof WorkflowStep)) {
        continue;
      }
      if (coordinate.step instanceof InitialStep) {
        continue;
      }
      // rebuild a graph from this step and make sure it has ends
      let childWorkflowCoordinates = [];
      let clientIdsSeen = {};
      childWorkflowCoordinates.push(coordinate);
      this.generateStepGraphFor(coordinate, childWorkflowCoordinates, clientIdsSeen, coordinate.step, null);
      if (!this.coordinatesHaveEnds(childWorkflowCoordinates) && !coordinate.isTermination) {
        if (brokenCoords.includes(coordinate.parent)){
          brokenCoords.splice(brokenCoords.indexOf(coordinate.parent), 1);
        }
        brokenCoords.push(coordinate);

      }
    }
    return brokenCoords;
  }

  private coordinatesHaveEnds(coordinates: DiagramElement[]) {
    // loop through each and check for to_client_id on each action to be null or empty string
    for (let coordinate of coordinates) {
      // if workflow coordinate, check all actions for ends (should only be one)
      if (coordinate instanceof StepElement && coordinate.step instanceof WorkflowStepActionJumpTo) {
        return true;
      }
    }
    return false;
  }

  // if you think about WorkflowSteps as nodes, and WorkflowStepActions as links,
  // this method traverses that (mathematical) graph and turns it into
  // an array of (diagram) Elements
  private generateStepGraphFor(parent: DiagramElement, workflowElements: DiagramElement[], clientIdsSeen: object, step: IWorkflowStepContainer, graphParent: DiagramElement): void {
    clientIdsSeen[step.client_id] = true;

    const queue = [{step: step, coordinate: parent}];
    while (queue.length > 0) {
      const s = queue.shift();
      const nextWorkflowSteps = s.step.nextSteps;
      for (let i=0; i < nextWorkflowSteps.length; i++) {
        const isTermination = !(nextWorkflowSteps[i] instanceof this.stepClass) || clientIdsSeen[nextWorkflowSteps[i].client_id];
        let workflowElement = null;
        if (nextWorkflowSteps[i] instanceof WorkflowStepAction) {
          workflowElement = new ActionCoordinate(
            s.coordinate,
            <WorkflowStepAction>nextWorkflowSteps[i],
            null,
          );
        } else if (nextWorkflowSteps[i] instanceof ProcessFlowStepAction) {
          workflowElement = new ProcessFlowActionCoordinate(
            s.coordinate,
            <ProcessFlowStepAction>nextWorkflowSteps[i],
            graphParent,
          );
        } else if (nextWorkflowSteps[i] instanceof ProcessFlowStepJumpTo) {
          workflowElement = new ProcessFlowEndElement(
            s.coordinate,
            <ProcessFlowStepJumpTo>nextWorkflowSteps[i],
            isTermination,
            graphParent
          )
        } else {
          workflowElement = new this.stepElementClass(
            s.coordinate,
            <WorkflowStep>nextWorkflowSteps[i],
            isTermination,
            graphParent,
            { isLinkConfigurable: this.isLinkConfigurable(nextWorkflowSteps[i]) }
          );
        }
        workflowElements.push(workflowElement);
        if (nextWorkflowSteps[i]) {
          clientIdsSeen[nextWorkflowSteps[i].client_id] = true;
          if (!workflowElement.isTermination) {
            queue.push({step: nextWorkflowSteps[i], coordinate: workflowElement});

            // Process sub graph, if applicable
            if (workflowElement.hasSubGraph) {
              const initialElement = new this.initialElementClass(null, workflowElement.step.initialStep, false, workflowElement);
              const childElements: DiagramElement[] = [];
              childElements.push(initialElement);
              this.generateStepGraphFor(initialElement, childElements, clientIdsSeen, workflowElement.step.initialStep, workflowElement);
              this.calculateLayout(initialElement, [0,0]);
              workflowElement.subElements = childElements;
              for (const childElement of childElements) {
                workflowElements.push(childElement);
              }
            }
          }
        }
      }
    }
  }
}
